import { CheckIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { CreateGuard } from '@/components/guards/permission-guards';
import { Button } from '@/components/ui/button';
import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { RESOURCES } from '@/utils/permissions';

// Types
interface PurchaseOrderFormData {
  purchaseRequisitionId?: string;
  supplierId: string;
  items: Array<{
    inventoryId: string;
    quantity: number;
    unitPrice: number;
  }>;
  expectedDeliveryDate: string;
  notes?: string;
}

interface PurchaseRequisition {
  _id: string;
  requestedBy: {
    username: string;
    email: string;
  };
  createdAt: string;
  priority: 'low' | 'medium' | 'high';
  status: string;
  items: Array<{
    inventory: {
      _id: string;
      name: string;
    };
    requestedQuantity: number;
  }>;
}

interface Supplier {
  _id: string;
  businessName: string;
  contactName: string;
  email: string;
  contactNumber?: string;
}

interface Product {
  _id: string;
  name: string;
  measurementUnit: string;
  quantity: number;
  reorderLevel: number;
  price?: number;
}

interface PurchaseOrderCreateSheetProps {
  onPurchaseOrderCreated?: () => Promise<void>;
}

const totalSteps = 3;
const stepLabels = [
  'Requisition Information',
  'Supplier Information',
  'Products',
];

// Step indicator component
const StepIndicator: React.FC<{
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}> = ({ currentStep, totalSteps, stepLabels }) => {
  return (
    <div className='flex items-center justify-between mb-8'>
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isLast = stepNumber === totalSteps;

        return (
          <React.Fragment key={stepNumber}>
            <div className='flex flex-col items-center'>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCompleted
                    ? 'bg-success-60 text-white'
                    : isActive
                      ? 'bg-success-60 text-white'
                      : 'bg-gray-20 text-gray-60'
                }`}
              >
                {isCompleted ? <CheckIcon className='w-4 h-4' /> : stepNumber}
              </div>
              <span className='text-xs mt-2 text-gray-60 text-center'>
                {stepLabels[index]}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mx-4 ${
                  isCompleted || (isActive && stepNumber > 1)
                    ? 'bg-success-60'
                    : 'bg-gray-20'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PurchaseOrderCreateSheet: React.FC<PurchaseOrderCreateSheetProps> = ({
  onPurchaseOrderCreated,
}) => {
  const api = useEnhancedApi();
  const { get, post } = api;
  const { hideSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [purchaseRequisitions, setPurchaseRequisitions] = useState<
    PurchaseRequisition[]
  >([]);
  const [orderItems, setOrderItems] = useState([
    { inventoryId: '', quantity: 1, unitPrice: 0 },
  ]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );
  const [selectedRequisition, setSelectedRequisition] =
    useState<PurchaseRequisition | null>(null);

  const { control, handleSubmit, watch, trigger, reset } =
    useForm<PurchaseOrderFormData>({
      defaultValues: {
        purchaseRequisitionId: '',
        supplierId: '',
        items: [],
        expectedDeliveryDate: '',
        notes: '',
      },
      mode: 'onChange',
    });

  const watchedSupplierId = watch('supplierId');
  const watchedRequisitionId = watch('purchaseRequisitionId');

  // Navigation functions
  const handleNextStep = async () => {
    let fieldsToValidate: (keyof PurchaseOrderFormData)[] = [];

    if (currentStep === 1) {
      // Requisition step is optional, just move to next step
    } else if (currentStep === 2) {
      fieldsToValidate = ['supplierId', 'expectedDeliveryDate'];
    }

    const isValid =
      fieldsToValidate.length === 0 || (await trigger(fieldsToValidate));

    if (currentStep === 2 && !selectedSupplier) {
      showError('Please select a supplier');
      return;
    }

    if (currentStep === 3) {
      const validItems = orderItems.filter(
        item =>
          item.inventoryId !== '' && item.quantity > 0 && item.unitPrice > 0
      );
      if (validItems.length === 0) {
        showError('Please add at least one valid item');
        return;
      }
    }

    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    reset();
    setCurrentStep(1);
    setOrderItems([{ inventoryId: '', quantity: 1, unitPrice: 0 }]);
    setSelectedSupplier(null);
    setSelectedRequisition(null);
    hideSheet('create-purchase-order');
  };

  const handleSubmitForm = async () => {
    if (currentStep < totalSteps) {
      return handleNextStep();
    }

    // Final submission
    const validItems = orderItems.filter(
      item => item.inventoryId !== '' && item.quantity > 0 && item.unitPrice > 0
    );

    if (validItems.length === 0) {
      showError('Please add at least one valid item to the purchase order');
      return;
    }

    setIsLoading(true);
    try {
      const formData = watch();
      const submitData = {
        ...formData,
        items: validItems,
      };

      const response = await post('/purchase-order', submitData);

      if (response.success) {
        showSuccess(response.message || 'Purchase order created successfully');
        reset();
        setOrderItems([{ inventoryId: '', quantity: 1, unitPrice: 0 }]);
        setCurrentStep(1);
        hideSheet('create-purchase-order');

        if (onPurchaseOrderCreated) {
          await onPurchaseOrderCreated();
        }
      } else {
        showError(response.error?.message || 'Failed to create purchase order');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unknown error occurred';
      showError(`Failed to create purchase order: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Expose handlers to global window object for GlobalSheetRenderer
  useEffect(() => {
    const handlers = {
      handleNextStep,
      handlePrevStep,
      handleSubmit: handleSubmitForm,
      handleCancel,
      currentStep,
      totalSteps,
      isLoading,
    };

    // Store handlers globally
    if (!(window as any).purchaseOrderCreateHandlers) {
      (window as any).purchaseOrderCreateHandlers = {};
    }
    (window as any).purchaseOrderCreateHandlers = handlers;

    // Cleanup on unmount
    return () => {
      if ((window as any).purchaseOrderCreateHandlers) {
        delete (window as any).purchaseOrderCreateHandlers;
      }
    };
  }, [
    currentStep,
    totalSteps,
    isLoading,
    selectedSupplier,
    orderItems,
    selectedRequisition,
  ]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const [suppliersResponse, productsResponse, requisitionsResponse] =
          await Promise.all([
            get('/suppliers', { approvalStatus: 'approved' }),
            get('/inventories', { approvalStatus: 'approved' }),
            get('/purchase-requisition', { status: 'approved' }),
          ]);

        if (suppliersResponse.success) {
          setSuppliers(suppliersResponse.data?.suppliers?.docs || []);
        } else {
          showError('Failed to fetch suppliers');
        }

        if (productsResponse.success) {
          setProducts(productsResponse.data?.inventory?.docs || []);
        } else {
          showError('Failed to fetch products');
        }

        if (requisitionsResponse.success) {
          setPurchaseRequisitions(
            requisitionsResponse.data?.requisitions?.docs || []
          );
        } else {
          showError('Failed to fetch purchase requisitions');
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        showError(`Error loading data: ${errorMessage}. Please try again.`);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [get, showError]);

  // Update selected supplier when supplier ID changes
  useEffect(() => {
    const supplier = suppliers.find(s => s._id === watchedSupplierId);
    setSelectedSupplier(supplier || null);
  }, [watchedSupplierId, suppliers]);

  // Update selected requisition when requisition ID changes
  useEffect(() => {
    const requisition = purchaseRequisitions.find(
      r => r._id === watchedRequisitionId
    );
    setSelectedRequisition(requisition || null);

    // Pre-populate order items if requisition is selected
    if (requisition && requisition.items.length > 0) {
      const newItems = requisition.items.map(item => ({
        inventoryId: item.inventory._id,
        quantity: item.requestedQuantity,
        unitPrice: 0,
      }));
      setOrderItems(newItems);
    }
  }, [watchedRequisitionId, purchaseRequisitions]);

  const onSubmit = async () => {
    await handleSubmitForm();
  };

  // Order items management
  const addItem = () => {
    setOrderItems([
      ...orderItems,
      { inventoryId: '', quantity: 1, unitPrice: 0 },
    ]);
  };

  const removeItem = (index: number) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    }
  };

  const updateItem = (
    index: number,
    field: keyof (typeof orderItems)[0],
    value: string | number
  ) => {
    const updatedItems = orderItems.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setOrderItems(updatedItems);
  };

  // Options for dropdowns
  const supplierOptions = suppliers.map(supplier => ({
    value: supplier._id,
    label: `${supplier.businessName}${supplier.contactName ? ` (${supplier.contactName})` : ''}`,
  }));

  const productOptions = products.map(product => ({
    value: product._id,
    label: `${product.name} (Stock: ${product.quantity} ${product.measurementUnit})`,
  }));

  const requisitionOptions = purchaseRequisitions.map(req => ({
    value: req._id,
    label: `REQ-${req._id.slice(-6)} - ${req.requestedBy.username} (${req.priority})`,
  }));

  const urgencyOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  const totalAmount = orderItems.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0
  );

  // Get tomorrow's date as minimum delivery date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDeliveryDate = tomorrow.toISOString().split('T')[0];

  if (isLoadingData) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto'></div>
          <p className='mt-2 text-sm text-gray-50'>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <CreateGuard resource={RESOURCES.PURCHASE_ORDERS}>
      <div className='space-y-6'>
        {/* Step Indicator */}
        <StepIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          stepLabels={stepLabels}
        />

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          {/* Step 1: Requisition Information */}
          {currentStep === 1 && (
            <div className='space-y-4'>
              {/* Purchase Requisition Selection */}
              <div className='space-y-2'>
                <Label htmlFor='purchaseRequisitionId'>
                  Purchase Requisition
                </Label>
                <Controller
                  name='purchaseRequisitionId'
                  control={control}
                  render={({ field, fieldState }) => (
                    <FilterSelect
                      name='purchaseRequisitionId'
                      value={field.value}
                      options={requisitionOptions}
                      placeholder='Select Purchase Requisition'
                      error={fieldState?.error?.message}
                      onChange={field.onChange}
                      className='w-full'
                      disabled={purchaseRequisitions.length === 0}
                    />
                  )}
                />
                {purchaseRequisitions.length === 0 && (
                  <p className='text-sm text-orange-60'>
                    No approved purchase requisitions available.
                  </p>
                )}
              </div>

              {/* Display Requisition Details if selected */}
              {selectedRequisition && (
                <div className='bg-gray-5 p-4 rounded-lg space-y-3'>
                  <h4 className='text-sm font-medium text-gray-90'>
                    Requisition Information
                  </h4>

                  <div className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <span className='text-gray-60'>Requested By:</span>
                      <div className='font-medium text-gray-90'>
                        {selectedRequisition.requestedBy.username}
                      </div>
                    </div>

                    <div>
                      <span className='text-gray-60'>Request Date:</span>
                      <div className='font-medium text-gray-90'>
                        {new Date(
                          selectedRequisition.createdAt
                        ).toLocaleDateString()}
                      </div>
                    </div>

                    <div>
                      <span className='text-gray-60'>Urgency Level:</span>
                      <div
                        className={`font-medium capitalize ${
                          selectedRequisition.priority === 'high'
                            ? 'text-destructive-60'
                            : selectedRequisition.priority === 'medium'
                              ? 'text-orange-60'
                              : 'text-success-60'
                        }`}
                      >
                        {selectedRequisition.priority}
                      </div>
                    </div>
                  </div>

                  <div>
                    <span className='text-gray-60 text-sm'>
                      Requested Items:
                    </span>
                    <div className='mt-1 space-y-1'>
                      {selectedRequisition.items.map((item, index) => (
                        <div key={index} className='text-sm text-gray-90'>
                          • {item.inventory.name} - Qty:{' '}
                          {item.requestedQuantity}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Entry Fields (when no requisition selected) */}
              {!selectedRequisition && (
                <>
                  <div className='space-y-2'>
                    <Label htmlFor='requestedBy'>Requested By</Label>
                    <Input
                      placeholder='John Doe'
                      disabled
                      className='bg-gray-5'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='requestDate'>Request Date</Label>
                    <Input
                      type='date'
                      defaultValue={new Date().toISOString().split('T')[0]}
                      disabled
                      className='bg-gray-5'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='urgencyLevel'>Urgency Level</Label>
                    <FilterSelect
                      name='urgencyLevel'
                      value='medium'
                      options={urgencyOptions}
                      placeholder='Select Urgency Level'
                      onChange={() => {}}
                      className='w-full'
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Supplier Information */}
          {currentStep === 2 && (
            <div className='space-y-4'>
              {/* Supplier Selection */}
              <div className='space-y-2'>
                <Label htmlFor='supplierId'>Supplier *</Label>
                <Controller
                  name='supplierId'
                  control={control}
                  rules={{ required: 'Supplier is required' }}
                  render={({ field, fieldState }) => (
                    <FilterSelect
                      name='supplierId'
                      value={field.value}
                      options={supplierOptions}
                      placeholder='select supplier'
                      error={fieldState?.error?.message}
                      onChange={field.onChange}
                      className='w-full'
                      disabled={suppliers.length === 0}
                    />
                  )}
                />
                {suppliers.length === 0 && (
                  <p className='text-sm text-orange-60'>
                    No approved suppliers available. Please add suppliers first.
                  </p>
                )}
              </div>

              {/* Display Supplier Details if selected */}
              {selectedSupplier && (
                <div className='bg-gray-5 p-4 rounded-lg space-y-3'>
                  <h4 className='text-sm font-medium text-gray-90'>
                    Supplier Information
                  </h4>

                  <div className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <span className='text-gray-60'>Supplier Email:</span>
                      <div className='font-medium text-gray-90'>
                        {selectedSupplier.email}
                      </div>
                    </div>

                    <div>
                      <span className='text-gray-60'>
                        Supplier Contact Person:
                      </span>
                      <div className='font-medium text-gray-90'>
                        {selectedSupplier.contactName}
                      </div>
                    </div>

                    <div>
                      <span className='text-gray-60'>
                        Supplier Contact Number:
                      </span>
                      <div className='font-medium text-gray-90'>
                        {selectedSupplier.contactNumber || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Expected Delivery Date */}
              <div className='space-y-2'>
                <Label htmlFor='expectedDeliveryDate'>
                  Expected Date of Delivery *
                </Label>
                <Controller
                  name='expectedDeliveryDate'
                  control={control}
                  rules={{ required: 'Expected delivery date is required' }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      type='date'
                      min={minDeliveryDate}
                      error={fieldState?.error?.message}
                      placeholder='00 / 00 / 0000'
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Step 3: Products */}
          {currentStep === 3 && (
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <Label>Purchase Items *</Label>
                <Button
                  type='button'
                  onClick={addItem}
                  hierarchy='outlined'
                  size='sm'
                  className='flex items-center gap-2 text-success-60 hover:text-success-70 border-success-20 hover:border-success-30'
                >
                  <PlusIcon className='h-4 w-4' />
                  Add New Item
                </Button>
              </div>

              {products.length === 0 && (
                <div className='bg-orange-5 border border-orange-20 rounded-lg p-3'>
                  <p className='text-sm text-orange-70'>
                    No approved products available. Please add products to
                    inventory first.
                  </p>
                </div>
              )}

              {orderItems.map((item, index) => {
                const selectedProduct = products.find(
                  p => p._id === item.inventoryId
                );
                const subTotal = item.quantity * item.unitPrice;

                return (
                  <div key={index} className='border rounded-lg p-4 space-y-3'>
                    <div className='grid grid-cols-5 gap-4 items-end'>
                      {/* Item */}
                      <div className='space-y-2'>
                        <Label className='text-gray-60'>Item</Label>
                        <FilterSelect
                          name={`product-${index}`}
                          value={item.inventoryId}
                          options={productOptions}
                          placeholder='Plum Tomatoes - Distress'
                          onChange={value =>
                            updateItem(index, 'inventoryId', value)
                          }
                          className='w-full'
                          disabled={products.length === 0}
                        />
                      </div>

                      {/* Variant - Placeholder for now */}
                      <div className='space-y-2'>
                        <Label className='text-gray-60'>Variant</Label>
                        <FilterSelect
                          name={`variant-${index}`}
                          value=''
                          options={[{ value: 'variant', label: 'Variant' }]}
                          placeholder='Variant'
                          onChange={() => {}}
                          className='w-full'
                        />
                      </div>

                      {/* Quantity */}
                      <div className='space-y-2'>
                        <Label className='text-gray-60'>Quantity</Label>
                        <Input
                          type='number'
                          min='1'
                          value={item.quantity}
                          onChange={e =>
                            updateItem(
                              index,
                              'quantity',
                              Number(e.target.value)
                            )
                          }
                          placeholder='1'
                          className='text-center'
                        />
                      </div>

                      {/* Unit Price */}
                      <div className='space-y-2'>
                        <Label className='text-gray-60'>Unit Price</Label>
                        <Input
                          type='number'
                          step='0.01'
                          min='0'
                          value={item.unitPrice}
                          onChange={e =>
                            updateItem(
                              index,
                              'unitPrice',
                              Number(e.target.value)
                            )
                          }
                          placeholder='Unit Price'
                        />
                      </div>

                      {/* Sub Total */}
                      <div className='space-y-2'>
                        <Label className='text-gray-60'>Sub Total</Label>
                        <div className='h-10 px-3 py-2 border border-gray-20 rounded-md bg-gray-5 text-gray-50 flex items-center'>
                          {subTotal > 0
                            ? `₦${subTotal.toLocaleString()}`
                            : 'Amount'}
                        </div>
                      </div>
                    </div>

                    {/* Remove button */}
                    {orderItems.length > 1 && (
                      <div className='flex justify-end'>
                        <Button
                          type='button'
                          onClick={() => removeItem(index)}
                          hierarchy='secondary'
                          size='sm'
                          className='text-destructive-50 hover:text-destructive-70 hover:bg-destructive-5'
                        >
                          <TrashIcon className='h-4 w-4' />
                        </Button>
                      </div>
                    )}

                    {selectedProduct && (
                      <div className='text-xs text-gray-50 bg-gray-5 p-2 rounded'>
                        Available: {selectedProduct.quantity}{' '}
                        {selectedProduct.measurementUnit}
                        {selectedProduct.quantity <=
                          selectedProduct.reorderLevel && (
                          <span className='text-orange-50 ml-2'>
                            (Low stock)
                          </span>
                        )}
                        {selectedProduct.price && (
                          <span className='ml-4'>
                            Suggested price: ₦
                            {selectedProduct.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Notes */}
              <div className='space-y-2'>
                <Label htmlFor='notes'>Purchase Order Notes (Optional)</Label>
                <Controller
                  name='notes'
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      placeholder='Any special instructions, terms, or delivery requirements'
                      rows={3}
                    />
                  )}
                />
              </div>

              {/* Purchase Order Summary */}
              {totalAmount > 0 && (
                <div className='bg-gray-5 p-4 rounded-lg'>
                  <h4 className='text-sm font-medium text-gray-90 mb-3'>
                    Purchase Order Summary
                  </h4>
                  <div className='space-y-2 text-sm'>
                    <div className='flex justify-between'>
                      <span>Supplier:</span>
                      <span className='font-medium'>
                        {selectedSupplier?.businessName || 'Not selected'}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Items:</span>
                      <span>
                        {
                          orderItems.filter(item => item.inventoryId !== '')
                            .length
                        }{' '}
                        items
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Expected Delivery:</span>
                      <span>{watch('expectedDeliveryDate') || 'Not set'}</span>
                    </div>
                    <div className='flex justify-between font-semibold text-base border-t pt-2'>
                      <span>Total Amount:</span>
                      <span>₦{totalAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex justify-between pt-6 border-t'>
            {currentStep > 1 ? (
              <Button
                type='button'
                onClick={handlePrevStep}
                hierarchy='outlined'
                className='flex items-center gap-2'
                disabled={isLoading}
              >
                ← Back to {stepLabels[currentStep - 2]}
              </Button>
            ) : (
              <div />
            )}

            <Button
              type='button'
              onClick={handleSubmitForm}
              className='bg-success-60 hover:bg-success-70 text-white flex items-center gap-2'
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                  Processing...
                </>
              ) : currentStep === totalSteps ? (
                <>📋 Save Purchase Order</>
              ) : (
                <>Proceed to {stepLabels[currentStep]}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </CreateGuard>
  );
};

export default PurchaseOrderCreateSheet;
